import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { config } from '../../config';
import { AuthService } from '../auth.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-magic-link',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="magic-link-container">
      <div class="magic-link-card">
        <div *ngIf="true === processing" class="processing">
          <h1>Validating your magic link...</h1>
          <div class="spinner"></div>
        </div>
        
        <div *ngIf="false === processing" class="result">
          <div *ngIf="true === success" class="success">
            <h1>Success!</h1>
            <p>Your magic link has been validated successfully.</p>
            <p>You can now return to your original browser window and continue.</p>
            <p class="info-text">The original window should be automatically updated.</p>
            <button class="close-button" (click)="closeWindow()">Close Window</button>
          </div>
          
          <div *ngIf="false === success" class="error">
            <h1>{{ errorMessage.includes('already logged in') ? 'Already Authenticated' : 'Oops!' }}</h1>
            <p>{{ errorMessage }}</p>
            <p *ngIf="!errorMessage.includes('already logged in')">Please try requesting a new magic link.</p>
            <p *ngIf="errorMessage.includes('already logged in')">You will be redirected to the dashboard shortly.</p>
            <button class="close-button" (click)="closeWindow()">{{ errorMessage.includes('already logged in') ? 'Go to Dashboard' : 'Close Window' }}</button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .magic-link-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background: var(--ap-bg, #F6F7F9);
    }

    .magic-link-card {
      background: var(--ap-surface, #fff);
      padding: 2rem;
      border-radius: var(--ap-radius-lg, 14px);
      border: 1px solid var(--ap-border, #E3E6EB);
      width: 90%;
      max-width: 400px;
      text-align: center;
    }

    h1 {
      margin-bottom: 1rem;
      color: var(--ap-text, #0B1220);
      font-size: 20px;
      font-weight: 600;
    }

    p {
      margin-bottom: 1rem;
      color: var(--ap-text-soft, #4B5563);
      font-size: 14px;
    }

    .info-text {
      font-style: italic;
      font-size: 13px;
      color: var(--ap-text-muted, #6B7280);
      margin-top: 1rem;
    }

    .success h1 {
      color: var(--ap-success, #047A55);
    }

    .error h1 {
      color: var(--ap-danger, #B42318);
    }

    .spinner {
      display: inline-block;
      width: 40px;
      height: 40px;
      border: 3px solid rgba(47,91,218,.2);
      border-radius: 50%;
      border-top-color: var(--ap-primary, #2F5BDA);
      animation: spin 1s ease-in-out infinite;
      margin: 1rem auto;
    }

    .close-button {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      border: none;
      border-radius: var(--ap-radius-sm, 6px);
      padding: 0 20px;
      height: 36px;
      font-size: 14px;
      font-weight: 550;
      cursor: pointer;
      margin-top: 1rem;
      transition: background 0.15s;
    }

    .close-button:hover {
      background: var(--ap-primary-ink, #1E3FA8);
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `]
})
export class MagicLinkComponent implements OnInit, OnDestroy {
  processing: boolean = true;
  success: boolean = false;
  errorMessage: string = 'The magic link is invalid or has expired.';
  
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private authService: AuthService
  ) {
    console.log('MagicLinkComponent initialized');
  }
  
  // This flag tracks whether we've detected a valid token process
  private validTokenProcess = false;
  private userSubscription: Subscription | null = null;

  ngOnInit(): void {
    console.log('MagicLinkComponent.ngOnInit called');
    console.log('Current URL:', window.location.href);
    
    // PROTECTION: Check if user is already authenticated
    const currentUser = this.authService.currentUserValue;
    if (currentUser && this.authService.isLoggedIn) {
      console.log('User is already authenticated - redirecting to dashboard to prevent logout');
      this.processing = false;
      this.success = false;
      this.errorMessage = 'You are already logged in. Magic link authentication is not needed.';
      
      // Optional: Redirect to dashboard after a delay
      setTimeout(() => {
        this.router.navigate(['/dashboard']);
      }, 3000);
      
      return; // Exit early to prevent magic link processing
    }
    
    // First, check if a successful magic link validation just happened
    const magicLinkSuccess = sessionStorage.getItem('magic_link_success');
    if (magicLinkSuccess === 'true') {
      console.log('Found magic_link_success flag - token was validated successfully');
      // Clear the flag immediately to prevent reuse
      sessionStorage.removeItem('magic_link_success');
      this.processing = false;
      this.success = true;
      return;
    }
    
    // Second, check if a token is currently being validated (by AuthService)
    const tokenBeingValidated = sessionStorage.getItem('token_being_validated');
    if (tokenBeingValidated === 'true') {
      console.log('Token is currently being validated');
      
      // Subscribe to validation status from AuthService
      this.userSubscription = this.authService.tokenValidationStatus$.subscribe(status => {
        console.log('Received token validation status:', status);
        if (status !== null) {
          // We got a validation result, process is complete
          this.processing = false;
          this.success = status === true;
          if (!this.success) {
            this.errorMessage = 'The magic link is invalid or has expired.';
          }
          // Clear the processing flag
          sessionStorage.removeItem('token_being_validated');
        }
      });
      
      // Also subscribe to user login events as backup
      const userSub = this.authService.currentUser$.subscribe(user => {
        if (user) {
          console.log('User logged in via magic link');
          this.processing = false;
          this.success = true;
          userSub.unsubscribe();
        }
      });
      
      return;
    }
    
    // Finally, look for a token in the URL
    const token = this.route.snapshot.queryParamMap.get('token');
    console.log('Token from route:', token);
    
    if (token) {
      // We found a token, process it
      console.log('Found token in URL, initializing validation');
      sessionStorage.setItem('token_being_validated', 'true');
      this.processing = true;
    } else {
      // No token found and not in a validation flow - this is an error
      console.log('No token found in any source');
      this.processing = false;
      this.success = false;
      this.errorMessage = 'No token provided in the URL.';
    }
    
    // Only initialize WebSocket and validate if we have a token and need to process it ourselves
    // (the AuthService hasn't already done it)
    if (token && this.processing) {
      // Initialize WebSocket for magic link validation
      this.authService.initializeWebSocketForMagicLink();
      
      // Directly call the server to validate the token
      // The server will broadcast the validation result via WebSocket
      // to the original browser window
      console.log('Validating token:', token);
      this.authService.validateToken(token).subscribe({
      next: (response) => {
        console.log('Token validation response:', response);
        if (true === response.success) {
          this.processing = false;
          this.success = true;

          // Broadcast authentication success to other tabs (original login tab)
          const currentUser = this.authService.currentUserValue;
          if (currentUser && response.token) {
            this.authService.broadcastMagicLinkSuccess(currentUser, response.token);
          }
        } else {
          this.processing = false;
          this.success = false;
          this.errorMessage = response.message || 'Token validation failed.';
        }
      },
      error: (err) => {
        console.error('Token validation error:', err);
        this.processing = false;
        this.success = false;
        this.errorMessage = 'Failed to validate the magic link. Please try again.';
      }
    });
    }
  }
  
  closeWindow(): void {
    // Check if this is the "already authenticated" case
    if (this.errorMessage && this.errorMessage.includes('already logged in')) {
      console.log('Navigating to dashboard for already authenticated user');
      this.router.navigate(['/dashboard']);
      return;
    }
    
    // Try to close the window first
    const closed = window.close();
    
    // If window.close() didn't work (returns false or undefined in some browsers)
    // Handle alternative behavior - redirect to home page after a short delay
    setTimeout(() => {
      if (document.visibilityState !== 'hidden') {
        console.log('Window close failed, redirecting to home');
        window.location.href = '/';
      }
    }, 300);
  }
  
  ngOnDestroy(): void {
    // Clean up subscription when component is destroyed
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
      this.userSubscription = null;
    }
  }
}
